import z from "zod";

import { SIZES } from "@frontend/styles/constants";

import { TOGGLE_GROUP_VARIANTS } from "./constants";

const commaSeparatedList = z
  .string()
  .optional()
  .transform((value) =>
    value === undefined
      ? undefined
      : value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
  );

export const toggleGroupDataSchema = z.object({
  defaultValue: commaSeparatedList.describe(
    "Comma-separated list of initially pressed toggle values. Read once, at mount — use `value` instead to change pressed toggles after the initial render.",
  ),
  value: commaSeparatedList.describe(
    "Comma-separated list of currently pressed toggle values. Settable at any time — e.g. `el.setAttribute('value', 'bold,italic')` or `el.setValue(['bold'])` — to change pressed toggles externally.",
  ),
  toggles: z
    .array(
      z.object({
        value: z.string().describe("Unique identifier for this toggle."),
        label: z.string().describe("Visible label shown on the toggle."),
        icon: z
          .string()
          .optional()
          .describe("Optional icon name shown next to the label."),
      }),
    )
    .default([])
    .describe(
      "Toggles parsed from the light DOM (<rwc-toggle-group-item> elements inside <rwc-toggle-group>) — not a Twig-authored attribute.",
    ),
  multiple: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false"),
  disabled: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false"),
  orientation: z.enum(["horizontal", "vertical"]).optional(),
  variant: z.enum(TOGGLE_GROUP_VARIANTS).optional(),
  size: z.enum(SIZES).optional(),
  iconOnly: z
    .string()
    .optional()
    .transform((value) => value !== undefined && value !== "false")
    .describe(
      "Shows each toggle's icon alone, its label becoming the accessible name and a tooltip. Toggles without an icon keep their label.",
    ),
  onValueChange: z
    .custom<(value: string[]) => void>()
    .optional()
    .describe(
      "Internal callback bound by readToggleGroupSlotted — not a Twig-authored attribute, passed through the same raw-props pipeline.",
    ),
});

export type ToggleGroupDataProps = z.infer<typeof toggleGroupDataSchema>;
